// What an effect needs to know about the picture under it: where the sky is
// dark, where light catches water, where the warmest light is, where the land
// starts. Measured once from a small copy of the picture laid out the way the
// screen shows it (covering, centred), so a place on the map is the same place
// on the screen. Pictures come from the board's own address, so reading their
// pixels is allowed.

import type { RGB } from "../scenes";

export interface PictureMap {
  /** a coarse grid of the picture as shown, for where things are */
  gw: number;
  gh: number;
  lum: Float32Array;
  rgb: Uint8ClampedArray;
  /** a finer grid, for specks of light: stars, glints */
  fw: number;
  fh: number;
  fine: Float32Array;
  /** the fine grid blurred, for what surrounds a speck */
  soft: Float32Array;
  /** the picture's mean brightness, 0 to 1 */
  mean: number;
  /** for each column of the fine grid, how far down the sky reaches before trees, hills or buildings stand against it, 0 to 1 */
  skyline: Float32Array;
}

/** A box on the screen, 0 to 1: left, top, right, bottom. */
export type Box = [number, number, number, number];

const cache = new Map<string, Promise<PictureMap | null>>();

/** The picture's map for a screen of this shape. Kept, so changing back costs nothing. */
export function mapPicture(src: string, aspect: number): Promise<PictureMap | null> {
  const key = `${src}|${aspect.toFixed(2)}`;
  let hit = cache.get(key);
  if (!hit) {
    hit = measure(src, aspect).catch(() => null);
    cache.set(key, hit);
    if (cache.size > 24) cache.delete(cache.keys().next().value!);
  }
  return hit;
}

async function measure(src: string, aspect: number): Promise<PictureMap | null> {
  const img = new Image();
  img.decoding = "async";
  img.src = src;
  await img.decode();
  if (!img.naturalWidth || !img.naturalHeight) return null;
  const fw = 640;
  const fh = Math.max(16, Math.round(fw / Math.max(0.3, aspect)));
  const c = document.createElement("canvas");
  c.width = fw;
  c.height = fh;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  // the same crop the screen makes: cover, centred
  const s = Math.max(fw / img.naturalWidth, fh / img.naturalHeight);
  const dw = img.naturalWidth * s;
  const dh = img.naturalHeight * s;
  ctx.drawImage(img, (fw - dw) / 2, (fh - dh) / 2, dw, dh);
  const d = ctx.getImageData(0, 0, fw, fh).data;
  const fine = new Float32Array(fw * fh);
  for (let i = 0; i < fw * fh; i++) fine[i] = (0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2]) / 255;
  const soft = boxBlur(fine, fw, fh, 5);
  const gw = 48;
  const gh = Math.max(8, Math.round(gw / Math.max(0.3, aspect)));
  const lum = new Float32Array(gw * gh);
  const rgb = new Uint8ClampedArray(gw * gh * 3);
  let total = 0;
  for (let j = 0; j < gh; j++) {
    for (let i = 0; i < gw; i++) {
      const x0 = Math.floor((i * fw) / gw);
      const x1 = Math.floor(((i + 1) * fw) / gw);
      const y0 = Math.floor((j * fh) / gh);
      const y1 = Math.floor(((j + 1) * fh) / gh);
      let r = 0;
      let g = 0;
      let b = 0;
      let l = 0;
      let n = 0;
      for (let y = y0; y < y1; y++)
        for (let x = x0; x < x1; x++) {
          const p = y * fw + x;
          r += d[p * 4];
          g += d[p * 4 + 1];
          b += d[p * 4 + 2];
          l += fine[p];
          n++;
        }
      const k = j * gw + i;
      lum[k] = n ? l / n : 0;
      rgb[k * 3] = n ? r / n : 0;
      rgb[k * 3 + 1] = n ? g / n : 0;
      rgb[k * 3 + 2] = n ? b / n : 0;
      total += lum[k];
    }
  }
  return { gw, gh, lum, rgb, fw, fh, fine, soft, mean: total / (gw * gh), skyline: skylineOf(fine, soft, fw, fh) };
}

/**
 * Where the sky stops in each column: going down from the top, the first place
 * that stays much darker than the sky, as the silhouette of a tree, a ridge or
 * a roof does. Light in the sky (an aurora, a shooting star) belongs above it,
 * and a cloud's shadow below it. Smoothed a little, so one dark pixel of cloud
 * does not cut the sky off, while a single tall pine still does.
 */
function skylineOf(fine: Float32Array, soft: Float32Array, fw: number, fh: number): Float32Array {
  const top: number[] = [];
  for (let j = 0; j < Math.floor(fh * 0.3); j++) for (let i = 0; i < fw; i += 4) top.push(soft[j * fw + i]);
  top.sort((a, b) => a - b);
  const sky = top[Math.floor(top.length / 2)] ?? 0.3;
  const dark = Math.min(0.2, Math.max(0.035, sky * 0.45));
  const raw = new Float32Array(fw);
  for (let i = 0; i < fw; i++) {
    raw[i] = 1;
    for (let j = 1; j < fh - 6; j++) {
      if (fine[j * fw + i] >= dark) continue;
      let run = 0;
      for (let k = 1; k <= 6; k++) run += fine[(j + k) * fw + i];
      if (run / 6 < dark * 1.3) {
        raw[i] = j / fh;
        break;
      }
    }
  }
  const out = new Float32Array(fw);
  for (let i = 0; i < fw; i++) {
    const near = [raw[Math.max(0, i - 1)], raw[i], raw[Math.min(fw - 1, i + 1)]].sort((a, b) => a - b);
    out[i] = near[1];
  }
  return out;
}

const masks = new WeakMap<PictureMap, HTMLCanvasElement>();

/** The sky as a mask, clear above the skyline and gone below it with a soft edge, for drawing light only in the sky. */
export function skyMask(m: PictureMap): HTMLCanvasElement {
  const kept = masks.get(m);
  if (kept) return kept;
  const c = document.createElement("canvas");
  c.width = m.fw;
  c.height = m.fh;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(m.fw, m.fh);
  const feather = 0.025;
  for (let i = 0; i < m.fw; i++) {
    const edge = m.skyline[i];
    for (let j = 0; j < m.fh; j++) {
      const y = j / m.fh;
      const a = y < edge - feather ? 1 : y > edge ? 0 : (edge - y) / feather;
      img.data[(j * m.fw + i) * 4 + 3] = Math.round(a * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  masks.set(m, c);
  return c;
}

/** Whether a place on the screen is open sky, clear of the skyline by margin. */
export function inSky(m: PictureMap, x: number, y: number, margin = 0.04): boolean {
  return y < m.skyline[Math.min(m.fw - 1, Math.max(0, Math.floor(x * m.fw)))] - margin;
}

/** How far down the sky reaches across most of the picture: the middle of its skyline. */
export function skyDepth(m: PictureMap): number {
  const s = [...m.skyline].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

/** A grid blurred by a square of side 2r + 1, done as two passes. */
function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    let acc = 0;
    for (let x = -r; x <= r; x++) acc += src[y * w + Math.min(w - 1, Math.max(0, x))];
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = acc / (2 * r + 1);
      acc += src[y * w + Math.min(w - 1, x + r + 1)] - src[y * w + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / (2 * r + 1);
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
    }
  }
  return out;
}

const cell = (m: PictureMap, x: number, y: number) => Math.min(m.gh - 1, Math.max(0, Math.floor(y * m.gh))) * m.gw + Math.min(m.gw - 1, Math.max(0, Math.floor(x * m.gw)));

/** How bright the picture is at a place on the screen, 0 to 1. */
export function lumAt(m: PictureMap, x: number, y: number): number {
  const gx = Math.min(m.gw - 1.001, Math.max(0, x * m.gw - 0.5));
  const gy = Math.min(m.gh - 1.001, Math.max(0, y * m.gh - 0.5));
  const i = Math.floor(gx);
  const j = Math.floor(gy);
  const fx = gx - i;
  const fy = gy - j;
  const at = (a: number, b: number) => m.lum[b * m.gw + a];
  const top = at(i, j) + (at(i + 1, j) - at(i, j)) * fx;
  const bot = at(i, j + 1) + (at(i + 1, j + 1) - at(i, j + 1)) * fx;
  return top + (bot - top) * fy;
}

/** The picture's color at a place on the screen. */
export function rgbAt(m: PictureMap, x: number, y: number): RGB {
  const k = cell(m, x, y) * 3;
  return [m.rgb[k], m.rgb[k + 1], m.rgb[k + 2]];
}

/**
 * Specks of light in a box: points brighter than all around them and clearly
 * brighter than their surroundings. Stars in a night sky, glints on water.
 * darkAround keeps only those whose surroundings are darker than it; inSky
 * keeps only those above the skyline, never a gap between a tree's branches.
 */
export function specks(m: PictureMap, box: Box, o: { max: number; min: number; lift: number; darkAround?: number; inSky?: boolean }): { x: number; y: number; b: number }[] {
  const out: { x: number; y: number; b: number }[] = [];
  const i0 = Math.max(1, Math.floor(box[0] * m.fw));
  const i1 = Math.min(m.fw - 1, Math.ceil(box[2] * m.fw));
  const j0 = Math.max(1, Math.floor(box[1] * m.fh));
  const j1 = Math.min(m.fh - 1, Math.ceil(box[3] * m.fh));
  for (let j = j0; j < j1; j++) {
    for (let i = i0; i < i1; i++) {
      const p = j * m.fw + i;
      const v = m.fine[p];
      if (v < o.min) continue;
      const around = m.soft[p];
      if (v - around < o.lift) continue;
      if (o.darkAround !== undefined && around > o.darkAround) continue;
      if (o.inSky && j / m.fh > m.skyline[i] - 0.015) continue;
      let top = true;
      for (let dy = -1; dy <= 1 && top; dy++) for (let dx = -1; dx <= 1; dx++) if ((dx || dy) && m.fine[p + dy * m.fw + dx] > v) top = false;
      if (top) out.push({ x: (i + 0.5) / m.fw, y: (j + 0.5) / m.fh, b: v - around });
    }
  }
  out.sort((a, b) => b.b - a.b);
  return out.slice(0, o.max);
}

/**
 * The brightest place in a box, leaning to warm light by warm (0 to 1), smoothed
 * so one hot pixel does not win. aside leans away from the middle of the
 * screen, where the tiles would hide it.
 */
export function hottest(m: PictureMap, box: Box, warm = 0, aside = false): { x: number; y: number; rgb: RGB; lum: number } | null {
  let best = -1;
  let at: { x: number; y: number; rgb: RGB; lum: number } | null = null;
  const i0 = Math.max(0, Math.floor(box[0] * m.gw));
  const i1 = Math.min(m.gw, Math.ceil(box[2] * m.gw));
  const j0 = Math.max(0, Math.floor(box[1] * m.gh));
  const j1 = Math.min(m.gh, Math.ceil(box[3] * m.gh));
  for (let j = j0; j < j1; j++) {
    for (let i = i0; i < i1; i++) {
      let s = 0;
      let n = 0;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          const a = i + dx;
          const b = j + dy;
          if (a < 0 || b < 0 || a >= m.gw || b >= m.gh) continue;
          const k = b * m.gw + a;
          s += m.lum[k] + warm * Math.max(0, m.rgb[k * 3] - m.rgb[k * 3 + 2]) / 255;
          n++;
        }
      let score = s / n;
      if (aside) {
        const dx = ((i + 0.5) / m.gw - 0.5) / 0.3;
        const dy = ((j + 0.5) / m.gh - 0.5) / 0.28;
        score *= 1 - 0.55 * Math.exp(-(dx * dx + dy * dy));
      }
      if (score > best) {
        best = score;
        const k = j * m.gw + i;
        at = { x: (i + 0.5) / m.gw, y: (j + 0.5) / m.gh, rgb: [m.rgb[k * 3], m.rgb[k * 3 + 1], m.rgb[k * 3 + 2]], lum: m.lum[k] };
      }
    }
  }
  return at;
}

/** The mean color of a box, counting only places whose brightness is between lo and hi. */
export function average(m: PictureMap, box: Box, lo = 0, hi = 1): RGB | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let j = Math.max(0, Math.floor(box[1] * m.gh)); j < Math.min(m.gh, Math.ceil(box[3] * m.gh)); j++)
    for (let i = Math.max(0, Math.floor(box[0] * m.gw)); i < Math.min(m.gw, Math.ceil(box[2] * m.gw)); i++) {
      const k = j * m.gw + i;
      if (m.lum[k] < lo || m.lum[k] > hi) continue;
      r += m.rgb[k * 3];
      g += m.rgb[k * 3 + 1];
      b += m.rgb[k * 3 + 2];
      n++;
    }
  return n ? [Math.round(r / n), Math.round(g / n), Math.round(b / n)] : null;
}

/**
 * Where the land starts, 0 to 1 down the screen: the row where the picture
 * steps most from a lighter sky to a darker ground, between a quarter and
 * three quarters of the way down. A picture with no such step says 0.5.
 */
export function horizon(m: PictureMap): number {
  const rows = Array.from({ length: m.gh }, (_, j) => {
    let s = 0;
    for (let i = 0; i < m.gw; i++) s += m.lum[j * m.gw + i];
    return s / m.gw;
  });
  let best = 0;
  let at = 0.5;
  for (let j = Math.floor(m.gh * 0.25); j < Math.floor(m.gh * 0.75); j++) {
    const above = (rows[j - 2] + rows[j - 1]) / 2;
    const below = (rows[j + 1] + rows[j + 2]) / 2;
    const step = above - below;
    if (step > best) {
      best = step;
      at = j / m.gh;
    }
  }
  return best > 0.04 ? at : 0.5;
}
